import type { PropsWithChildren } from "react";

import { useTranslation } from "pro-features/components/WithTranslation";
import type { ICountryObject } from "app/services/CountryService";

import { useCrmEnrichmentContext } from "../../../contexts/CrmEnrichmentContext";
import {
    type CrmEnrichmentJobObject,
    type CrmEnrichmentJobType,
    type CrmEnrichmentJobFilterType,
    type CrmEnrichmentJobRefreshPeriod,
    type CrmEnrichmentSource,
    CrmEnrichmentWizardSteps,
} from "../../../types/types";
import CrmEnrichmentJobReviewBlock from "../CrmEnrichmentJobReviewBlock";
import CrmEnrichmentJobReviewFilters from "../CrmEnrichmentJobReviewFilters";
import CrmEnrichmentJobReviewSettings from "../CrmEnrichmentJobReviewSettings";
import CrmEnrichmentJobWizardHeader from "../CrmEnrichmentJobWizardHeader";

import {
    StyledCrmEnrichmentJobReviewContainer,
    StyledCrmEnrichmentJobReviewWrapper,
    StyledCrmEnrichmentJobReviewValue,
} from "./styles";

type Props = PropsWithChildren<{
    selectedJobType: CrmEnrichmentJobType | "";
    selectedJobObject: CrmEnrichmentJobObject | "";
    selectedCountry: ICountryObject;
    selectedJobRefreshPeriod: CrmEnrichmentJobRefreshPeriod;
    selectedEnrichmentSource: CrmEnrichmentSource;
    isApplyFiltersSelected: boolean | null;
    filters: readonly CrmEnrichmentJobFilterType[];
    mappingsCount: number;
    onMoveToStep: (step: number) => void;
}>;

const CrmEnrichmentJobReviewWizardStep = ({
    children,
    selectedJobType,
    selectedJobObject,
    selectedJobRefreshPeriod,
    selectedEnrichmentSource,
    selectedCountry,
    filters,
    isApplyFiltersSelected,
    mappingsCount,
    onMoveToStep,
}: Props) => {
    const translate = useTranslation();

    const { i18nKeys } = useCrmEnrichmentContext();

    return (
        <>
            <CrmEnrichmentJobWizardHeader
                titleKey="si.crm-enrichment.job-wizard.review.title"
                descriptionKey="si.crm-enrichment.job-wizard.review.description"
            />

            <StyledCrmEnrichmentJobReviewContainer>
                <StyledCrmEnrichmentJobReviewWrapper>
                    <CrmEnrichmentJobReviewBlock
                        titleKey="si.crm-enrichment.job-wizard.review.type"
                        onEdit={() => onMoveToStep(CrmEnrichmentWizardSteps.TypeStep)}
                    >
                        {selectedJobType ? (
                            <StyledCrmEnrichmentJobReviewValue>
                                {translate(i18nKeys.type(selectedJobType))}
                            </StyledCrmEnrichmentJobReviewValue>
                        ) : null}
                    </CrmEnrichmentJobReviewBlock>

                    <CrmEnrichmentJobReviewBlock
                        titleKey="si.crm-enrichment.job-wizard.review.settings"
                        onEdit={() => onMoveToStep(CrmEnrichmentWizardSteps.SettingsStep)}
                    >
                        <CrmEnrichmentJobReviewSettings
                            translationKeys={i18nKeys}
                            selectedCountry={selectedCountry}
                            selectedJobObject={selectedJobObject}
                            selectedJobRefreshPeriod={selectedJobRefreshPeriod}
                            selectedEnrichmentSource={selectedEnrichmentSource}
                            selectedJobType={selectedJobType}
                        />
                    </CrmEnrichmentJobReviewBlock>

                    <CrmEnrichmentJobReviewBlock
                        titleKey="si.crm-enrichment.job-wizard.filters.title"
                        onEdit={() => onMoveToStep(CrmEnrichmentWizardSteps.FiltersStep)}
                    >
                        <CrmEnrichmentJobReviewFilters
                            filters={filters}
                            isApplyFiltersSelected={isApplyFiltersSelected}
                            selectedJobObject={selectedJobObject}
                            translationKeys={i18nKeys}
                        />
                    </CrmEnrichmentJobReviewBlock>

                    <CrmEnrichmentJobReviewBlock
                        titleKey="si.crm-enrichment.job-wizard.legend.fields-mapping"
                        onEdit={() => onMoveToStep(CrmEnrichmentWizardSteps.FieldsMappingStep)}
                    >
                        <StyledCrmEnrichmentJobReviewValue>
                            {translate("si.crm-enrichment.job-wizard.review.fields-mapping.count", { count: String(mappingsCount) })}
                        </StyledCrmEnrichmentJobReviewValue>
                    </CrmEnrichmentJobReviewBlock>
                </StyledCrmEnrichmentJobReviewWrapper>

                {children}
            </StyledCrmEnrichmentJobReviewContainer>
        </>
    );
};

export default CrmEnrichmentJobReviewWizardStep;
